import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ArrowLeft, CreditCard, Eye, FileText, RefreshCw, User as UserIcon } from 'lucide-react';
import { InvoicePdfModal } from '@/components/shared/InvoicePdfModal';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  useGetTopupOrder,
  useRetryFiscal,
} from '@/services/api/endpoints/TopupOrderService';
import { formatDate, formatAmount } from '@/lib/utils';
import { TopupStatusBadge, PaymentMethodBadge, FiscalIndicator } from './OrdersPage';

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [pdfOpen, setPdfOpen] = useState(false);
  const { data, isLoading, isError } = useGetTopupOrder(id);
  const retry = useRetryFiscal();

  if (isLoading) return <div className="p-6 text-muted-foreground">{t('common.loading')}</div>;
  if (isError || !data) {
    return (
      <div className="p-6 space-y-3">
        <Link to="/orders" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
          <ArrowLeft className="w-4 h-4" /> {t('topupOrders.back')}
        </Link>
        <p>{t('topupOrders.notFound')}</p>
      </div>
    );
  }

  const customer = [data.user?.firstName, data.user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim() || data.user?.email || '—';

  const fiscalFailed = data.fiscalReceipt?.status === 'failed';

  return (
    <div className="space-y-6">
      <Link to="/orders" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">
        <ArrowLeft className="w-4 h-4" /> {t('topupOrders.back')}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle>
                {t('topupOrders.orderTitle')}{' '}
                <span className="font-mono text-sm text-muted-foreground">
                  {data.payspotOrderId}
                </span>
              </CardTitle>
              <TopupStatusBadge status={data.status} />
            </div>
          </CardHeader>
          <CardContent className="divide-y">
            <Row label={t('topupOrders.amount')}>
              <span className="font-semibold">
                {formatAmount(data.amount)} {data.currency}
              </span>
            </Row>
            <Row label={t('topupOrders.company')}>{data.company?.name || '—'}</Row>
            <Row label={t('topupOrders.date')}>{formatDate(data.createdAt)}</Row>
            <Row label={t('topupOrders.method')}>
              <PaymentMethodBadge order={data} />
            </Row>
            <Row label="Payspot tx ID">
              <code className="text-xs">{data.payspotTransactionId || '—'}</code>
            </Row>
            {data.completedAt && <Row label={t('topupOrders.completedAt')}>{formatDate(data.completedAt)}</Row>}
            {data.failureReason && (
              <Row label={t('topupOrders.failureReason')}>
                <span className="text-destructive text-sm">{data.failureReason}</span>
              </Row>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <UserIcon className="w-4 h-4" /> {t('topupOrders.customer')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              <p className="font-medium">{customer}</p>
              {data.user?.email && (
                <p className="text-sm text-muted-foreground">{data.user.email}</p>
              )}
              {data.user?.publicId && (
                <p className="text-xs text-muted-foreground font-mono">
                  ID: {data.user.publicId}
                </p>
              )}
              {data.user?.phone && (
                <p className="text-sm text-muted-foreground">{data.user.phone}</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" /> {t('topupOrders.fiscalReceipt')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 pt-0">
              {data.fiscalReceipt ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase text-muted-foreground tracking-wide">
                      {t('common.status')}
                    </span>
                    <FiscalIndicator fiscal={data.fiscalReceipt} />
                  </div>
                  {data.fiscalReceipt.invoiceNumber && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase text-muted-foreground tracking-wide">
                        {t('topupOrders.invoiceNumber')}
                      </span>
                      <code className="text-xs">{data.fiscalReceipt.invoiceNumber}</code>
                    </div>
                  )}
                  {data.fiscalReceipt.pdfUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => setPdfOpen(true)}
                    >
                      <Eye className="w-4 h-4 mr-1.5" />
                      {t('topupOrders.viewPdf') || 'View PDF'}
                    </Button>
                  )}
                  {fiscalFailed && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => {
                        retry.mutate(data.id, {
                          onSuccess: () => toast.success(t('topupOrders.retryQueued')),
                          onError: (err: any) =>
                            toast.error(err?.response?.data?.message || err?.message),
                        });
                      }}
                      disabled={retry.isPending}
                    >
                      <RefreshCw className="w-4 h-4 mr-1.5" />
                      {t('topupOrders.retry')}
                    </Button>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">{t('topupOrders.noFiscalYet')}</p>
              )}
            </CardContent>
          </Card>

          {data.savedCard && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="w-4 h-4" /> {t('topupOrders.card')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 pt-0">
                <p className="font-medium">
                  {data.savedCard.cardBrand || 'Card'} • {data.savedCard.maskedPan || '••••'}
                </p>
                {data.savedCard.expiryDate && (
                  <p className="text-sm text-muted-foreground">
                    {t('topupOrders.expires')}: {data.savedCard.expiryDate}
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {pdfOpen && data.fiscalReceipt?.pdfUrl && (
        <InvoicePdfModal
          pdfUrl={data.fiscalReceipt.pdfUrl}
          invoiceNumber={data.fiscalReceipt.invoiceNumber}
          onClose={() => setPdfOpen(false)}
        />
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3">
      <span className="text-xs uppercase text-muted-foreground tracking-wide">{label}</span>
      <div>{children}</div>
    </div>
  );
}
